import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

/**
 * 汎用アクション outbox (docs/03 §7.5-3 層1 / §7.5-7 の `kind` 駆動 outbox)。
 *
 * 投稿 (`features/posts/outbox.ts`) / ECW (`features/insights/ecwOutbox.ts`) と異なり、
 * 賛同・アンケート回答・検証回答・membership のような「**決定的 doc ID への小さな冪等書込**」を
 * 1 つの store に集約する。これらは共通して:
 *   - 送信先 doc ID が決定的 (uid または districtId) → 自然冪等 (docs/04 §2-2)
 *   - payload が小さい JSON (photo Blob 無し)
 *   - last-write-wins / coalesce で良い (連打しても **最終 intent** のみ送れば足りる)
 * という性質を持つため、`kind` discriminator + **決定的 `clientId` (= 送信先 doc path) を
 * primary key** にした 1 store で扱う。同一 `clientId` への再 enqueue は `put` で上書き (coalesce)
 * され、トグル連打や再編集がキュー内で 1 件に畳まれる (docs/03 §7.5-3「決定的 clientId →
 * 同一 doc へ setDoc(merge)＝冪等」と一致)。
 *
 * **安全性は冪等性が担保する**: Web Locks (flushLock.ts) による flush 直列化は「複数タブの
 * 無駄打ち防止」の最適化であって、安全性の必要条件ではない (docs/03 §7.5-3)。
 *
 * increment 1 で `kind: 'agree'`、increment 2 で `'survey'`、increment 3 で `'verification'` を追加。
 * membership は後続 increment で追加する (store schema は kind 非依存なので migration 不要)。
 *
 * **payload の構造 (kind 別)**:
 *   - agree: payload=null (本体は空、存在=同感。op で set/delete を表現)。
 *   - survey / verification: `setDoc(merge:false)` 全置換のため、payload に
 *     `{ body, serverTimestampFields, createOnlyTimestampFields }` を持たせる:
 *       - `body`: serverTimestamp 以外の確定フィールド (answers / participantUid 等)。
 *       - `serverTimestampFields`: flush 時に常に `serverTimestamp()` を入れる列 (例 updatedAt)。
 *       - `createOnlyTimestampFields`: flush 時に既存 doc があれば既存値を温存、無ければ
 *         `serverTimestamp()` を入れる列 (例 createdAt)。`serverTimestamp()` sentinel は IndexedDB に
 *         保存できないため、この「タイムスタンプ方針」をデータで表現し flush 側で generic に適用する
 *         (feature 固有の sender import を不要にして outbox を真に汎用に保つ)。
 */

/** 送信先 entity 種別。後続 increment で 'membership' を追加。 */
export type ActionOutboxKind = 'agree' | 'survey' | 'verification';

export interface ActionOutboxEntry {
  /**
   * 決定的キー (primary key)。**送信先 Firestore doc path** をそのまま使う。
   * 例 (agree): `districts/{districtId}/posts/{postId}/agrees/{uid}`。
   * 同一キーへの再 enqueue は `put` で上書き = coalesce + 冪等。
   */
  clientId: string;
  kind: ActionOutboxKind;
  /** 送信操作。'set' = setDoc / 'delete' = deleteDoc。 */
  op: 'set' | 'delete';
  /** 地区 ID (claim 前処理 / フィルタ / 楽観表示で使用)。 */
  districtId: string;
  /**
   * 送信に必要な入力 (sender が Firestore payload を再構築する素データ)。
   * `serverTimestamp()` 等の sentinel は IndexedDB に保存できないため、sender が flush 時に再生成する。
   * agree のように body が空 / op==='delete' の場合は null。
   */
  payload: Record<string, unknown> | null;
  /** 作成日時 (ローカル epoch ms、UI 表示 / 並び順用、送信時は sender が serverTimestamp で上書き)。 */
  createdLocalAt: number;
  /** 最後の送信試行のエラーメッセージ (UI 用、null = 未試行)。 */
  lastError: string | null;
  /** 試行回数 (MAX_ATTEMPTS 判定用)。 */
  attempts: number;
  /**
   * 送信ステータス (posts/outbox.ts と同じセマンティクス、optional)。
   *   - 'pending' (default): 未送信 + 試行中。online 復帰 / 起動 / 変化時に自動 flush 対象。
   *   - 'failed': MAX_ATTEMPTS 超過。auto-flush は skip、手動 flush / retry でのみ再試行。
   */
  status?: 'pending' | 'failed';
}

/** enqueue 時に呼び出し側が指定する最小フィールド (createdLocalAt 等は自動付与)。 */
export type ActionOutboxInput = Pick<
  ActionOutboxEntry,
  'clientId' | 'kind' | 'op' | 'districtId' | 'payload'
>;

/** 自動 retry の最大試行回数 (posts/outbox.ts・ecwOutbox.ts と統一)。 */
export const MAX_ATTEMPTS = 5;

const DB_NAME = 'citizen-walk-action-outbox';
const DB_VERSION = 1;
const STORE_NAME = 'actions';

interface ActionOutboxDB extends DBSchema {
  actions: {
    key: string;
    value: ActionOutboxEntry;
  };
}

let dbPromise: Promise<IDBPDatabase<ActionOutboxDB>> | null = null;

function getDb(): Promise<IDBPDatabase<ActionOutboxDB>> {
  if (!dbPromise) {
    dbPromise = openDB<ActionOutboxDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          // primary key = clientId (= 送信先 doc path)。autoIncrement にしない理由:
          // 同一送信先への再 enqueue を put で 1 件に coalesce するため。
          db.createObjectStore(STORE_NAME, { keyPath: 'clientId' });
        }
      },
    });
  }
  return dbPromise;
}

/**
 * BroadcastChannel: 他 tab で action outbox 変化を通知 (posts/outbox.ts と同パターン)。
 * BroadcastChannel は自タブに配信しないため、同一タブの購読者には local listener で直接通知する。
 */
const BROADCAST_CHANNEL_NAME = 'citizen-walk-action-outbox-changed';
let bc: BroadcastChannel | null = null;
function getBroadcastChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null;
  if (!bc) bc = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
  return bc;
}

const localListeners = new Set<() => void>();

function notifyChanged() {
  for (const cb of [...localListeners]) {
    try {
      cb();
    } catch {
      /* 1 つの listener 失敗が他に波及しないよう握りつぶす */
    }
  }
  getBroadcastChannel()?.postMessage({ type: 'action-outbox-changed' });
}

/** 同一タブ local + 他 tab BroadcastChannel の購読 (unmount 時 unsubscribe)。 */
export function subscribeActionOutboxChanges(callback: () => void): () => void {
  localListeners.add(callback);
  const channel = getBroadcastChannel();
  const handler = () => callback();
  channel?.addEventListener('message', handler);
  return () => {
    localListeners.delete(callback);
    channel?.removeEventListener('message', handler);
  };
}

/**
 * action を enqueue (upsert)。同一 `clientId` の未送信エントリは **最後の intent で上書き (coalesce)**。
 * 再 enqueue は新しい意図なので `attempts` / `status` / `lastError` をリセットする。
 */
export async function enqueueAction(input: ActionOutboxInput): Promise<void> {
  const db = await getDb();
  const entry: ActionOutboxEntry = {
    ...input,
    createdLocalAt: Date.now(),
    attempts: 0,
    lastError: null,
    status: 'pending',
  };
  await db.put(STORE_NAME, entry);
  notifyChanged();
}

/** action 全件取得 (createdLocalAt 昇順)。 */
export async function listActions(): Promise<ActionOutboxEntry[]> {
  const db = await getDb();
  const all = (await db.getAll(STORE_NAME)) as ActionOutboxEntry[];
  return all.sort((a, b) => a.createdLocalAt - b.createdLocalAt);
}

/** action 1 件取得 (clientId 直接 get、O(1))。未存在は undefined。 */
export async function getAction(clientId: string): Promise<ActionOutboxEntry | undefined> {
  const db = await getDb();
  return (await db.get(STORE_NAME, clientId)) as ActionOutboxEntry | undefined;
}

/** action 件数。 */
export async function countActions(): Promise<number> {
  const db = await getDb();
  return await db.count(STORE_NAME);
}

/** action 1 件削除 (送信成功時)。 */
export async function deleteAction(clientId: string): Promise<void> {
  const db = await getDb();
  await db.delete(STORE_NAME, clientId);
  notifyChanged();
}

/** action 1 件更新 (送信失敗時に lastError / attempts / status を更新)。 */
export async function updateAction(entry: ActionOutboxEntry): Promise<void> {
  const db = await getDb();
  await db.put(STORE_NAME, entry);
  notifyChanged();
}

/** action 全クリア (test 用 + 緊急時の手動操作)。 */
export async function clearActions(): Promise<void> {
  const db = await getDb();
  await db.clear(STORE_NAME);
  notifyChanged();
}

/**
 * test 用に internal state を reset (mock 切替時)。本番 bundle には含まれるが index 非公開。
 */
export function __resetActionOutboxDb() {
  dbPromise = null;
  localListeners.clear();
  if (bc) {
    bc.close();
    bc = null;
  }
}
